import { useState, useEffect, useCallback } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { API_BASE } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { useUiStore } from '../stores/uiStore'
import { AccountMenu } from './AccountMenu'
import ContextMenu from './ContextMenu'
import type { ContextMenuItem } from './ContextMenu'
import { Logo } from './Logo'
import '../styles/app-shell.css'

interface NavItem {
    to: string
    icon: string
    label: string
    badge?: string
    permission?: string  // hide this item unless the user holds this permission
}

interface NavSection {
    section: string
    items: NavItem[]
}

// Nav items are gated by permission keys from the backend catalogue (Core/Domain/Permissions.cs).
const NAV_ITEMS: NavSection[] = [
    {
        section: 'Dashboards',
        items: [
            { to: '/dashboard', icon: '⬛', label: 'Daily', permission: 'dashboard.view' },
            { to: '/dashboard/monthly', icon: '📅', label: 'Monthly', permission: 'dashboard.view' },
            { to: '/dashboard/custom', icon: '📆', label: 'Custom', permission: 'dashboard.view' },
        ],
    },
    {
        section: 'Reporting',
        items: [
            { to: '/inventory', icon: '📦', label: 'Inventory', permission: 'inventory.view' },
            { to: '/never-out', icon: '🔔', label: 'Never Out', permission: 'neverout.view' },
            { to: '/reports', icon: '📊', label: 'Scheduled Reports', permission: 'reports.view' },
            { to: '/reports/adventure-bucks', icon: '🪙', label: 'Adventure Bucks', permission: 'loyalty.audit.view' },
        ],
    },
    {
        // Every item here carries a permission from the "Purchase orders" category, so the
        // whole section disappears for users holding none of them (empty sections are dropped).
        section: 'Purchase Orders',
        items: [
            { to: '/purchase-orders/planning', icon: '🧾', label: 'Planning', permission: 'purchaseorders.create' },
        ],
    },
    {
        section: 'Roadmap',
        items: [
            { to: '/roadmap', icon: '🗺️', label: 'Board', permission: 'roadmap.view' },
            { to: '/admin/roadmap', icon: '🛠️', label: 'Moderate', permission: 'roadmap.admin' },
        ],
    },
    {
        section: 'Administration',
        items: [
            { to: '/inbox', icon: '📥', label: 'Access Requests', permission: 'access.approve' },
            { to: '/admin/users', icon: '👥', label: 'Users', permission: 'users.admin' },
            { to: '/admin/roles', icon: '🛡', label: 'Roles & permissions', permission: 'roles.admin' },
            { to: '/admin/stores', icon: '🏬', label: 'Stores', permission: 'stores.admin' },
            { to: '/admin/settings', icon: '⚙', label: 'System Admin', permission: 'system.settings' },
        ],
    },
    {
        section: 'Help & Support',
        items: [
            { to: '/docs', icon: '❓', label: 'Documentation' }
        ]
    }
]

export default function AppShell() {
    const [mobileOpen, setMobileOpen] = useState(false)
    const { user, isImpersonating, originalUser, stopImpersonation } = useAuthStore()
    const token = useAuthStore(s => s.accessToken)
    // Only treat it as impersonation when we're acting as a *different* user. Guards against
    // stale persisted state where the flag is set but the original and current user are the same.
    const isImpersonatingOther = isImpersonating && !!originalUser && originalUser.id !== user?.id
    const forcedLayout = useUiStore(s => s.forcedLayout)

    const [homepageDashboard, setHomepageDashboard] = useState<string | null>(null)

    const [isWinDesktop, setIsWinDesktop] = useState(window.innerWidth >= 768)
    useEffect(() => {
        const handleResize = () => setIsWinDesktop(window.innerWidth >= 768)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const displayMode = forcedLayout === 'auto' ? (isWinDesktop ? 'desktop' : 'mobile') : forcedLayout

    const can = (p: string) => user?.permissions?.includes(p) ?? false

    // Fetch user's homepage preference
    useEffect(() => {
        if (!token) return
        fetch(API_BASE + '/api/me/preferences', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data?.homepageDashboard) {
                    setHomepageDashboard(data.homepageDashboard)
                }
            })
            .catch(() => {})
    }, [token])

    // Dashboard route → type mapping
    const DASHBOARD_ROUTES: Record<string, string> = {
        '/dashboard': 'daily',
        '/dashboard/monthly': 'monthly',
        '/dashboard/custom': 'custom'
    }

    const toggleHomepage = useCallback(async (dashboardType: string) => {
        if (!token) return
        const isCurrentlyHomepage = (homepageDashboard ?? 'daily') === dashboardType
        const body = isCurrentlyHomepage
            ? { clearHomepage: true }
            : { homepageDashboard: dashboardType }
        try {
            const res = await fetch(API_BASE + '/api/me/preferences', {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            })
            if (res.ok) {
                const data = await res.json()
                setHomepageDashboard(data.homepageDashboard ?? null)
            }
        } catch { /* silent */ }
    }, [token, homepageDashboard])

    const getDashboardContextItems = useCallback((route: string): ContextMenuItem[] => {
        const type = DASHBOARD_ROUTES[route]
        if (!type) return []
        const effectiveHomepage = homepageDashboard ?? 'daily'
        return [
            {
                label: 'Homepage',
                icon: '🏠',
                checked: effectiveHomepage === type,
                onClick: () => toggleHomepage(type)
            }
        ]
    }, [homepageDashboard, toggleHomepage])

    // Fetch pending requests count for the badge
    const { data: pendingData } = useQuery<{ count: number }>({
        queryKey: ['access-inbox-pending-count'],
        queryFn: async () => {
            const token = useAuthStore.getState().accessToken
            const res = await fetch(API_BASE + '/api/admin/access-requests/pending-count', {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (!res.ok) throw new Error('Failed to fetch count')
            return res.json()
        },
        enabled: can('access.approve') && !!token, // Only fetch if they can approve access requests
        refetchInterval: 30_000 // Poll every 30s
    })

    const pendingCount = pendingData?.count ?? 0

    // Fetch pending roadmap submissions count for the admin nav badge
    const { data: roadmapPendingData } = useQuery<{ count: number }>({
        queryKey: ['admin-roadmap-pending-count'],
        queryFn: async () => {
            const token = useAuthStore.getState().accessToken
            const res = await fetch(API_BASE + '/api/admin/roadmap/pending-count', {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (!res.ok) throw new Error('Failed to fetch count')
            return res.json()
        },
        enabled: can('roadmap.admin') && !!token, // Only fetch for roadmap admins
        refetchInterval: 60_000
    })

    const roadmapPendingCount = roadmapPendingData?.count ?? 0

    const dynamicNavItems = NAV_ITEMS.map(section => ({
        ...section,
        items: section.items.map(item => {
            if (item.to === '/inbox' && pendingCount > 0) return { ...item, badge: String(pendingCount) }
            if (item.to === '/admin/roadmap' && roadmapPendingCount > 0) return { ...item, badge: String(roadmapPendingCount) }
            return item
        }),
    }))

    // No desktop collapse: the sidebar is always expanded on desktop, and mobile
    // gets the drawer instead. The old collapsed rail existed to hold a square logo
    // tile, and that variant is gone -- the one official lockup will not fit a rail.
    const sidebarClass = ['sidebar', mobileOpen ? 'open' : ''].filter(Boolean).join(' ')

    return (
        <div className="app-shell">
            {/* Mobile overlay */}
            {mobileOpen && displayMode === 'mobile' && (
                <div
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        zIndex: 40
                    }}
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={sidebarClass}>
                <div className="sidebar-header">
                    <Logo height={40} />
                </div>

                <nav className="sidebar-nav">
                    {/* Unassigned users only see store request link */}
                    {user?.role === 'Unassigned' && (
                        <NavLink
                            to="/my-stores"
                            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                        >
                            <span className="nav-icon">🏪</span>
                            <span className="nav-label">Request Store Access</span>
                        </NavLink>
                    )}

                    {dynamicNavItems
                        .map(s => ({ ...s, items: s.items.filter(i => !i.permission || can(i.permission)) }))
                        .filter(s => s.items.length > 0)
                        .map(section => (
                        <div key={section.section}>
                            <div className="nav-section-label">{section.section}</div>
                            {section.items.map(item => {
                                const isDashboard = !!DASHBOARD_ROUTES[item.to]
                                const navContent = (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        // Exact match for any item that has children below it,
                                        // else both the parent and the child highlight at once.
                                        end={item.to === '/dashboard' || item.to === '/reports'}
                                        className={({ isActive }) =>
                                            `nav-item${isActive ? ' active' : ''}`
                                        }
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        <span className="nav-icon">{item.icon}</span>
                                        <span className="nav-label">{item.label}</span>
                                        {item.badge && (
                                            <span className={`nav-badge ${item.to === '/inbox' ? 'danger' : ''}`}
                                                  style={item.to === '/inbox' ? { backgroundColor: 'var(--color-error)', color: 'white' } : {}}>
                                                {item.badge}
                                            </span>
                                        )}
                                    </NavLink>
                                )

                                if (isDashboard) {
                                    return (
                                        <ContextMenu key={item.to} items={getDashboardContextItems(item.to)}>
                                            {navContent}
                                        </ContextMenu>
                                    )
                                }
                                return navContent
                            })}
                        </div>
                    ))}
                </nav>

                {/* Sidebar footer — role badge only, sign-out moved to header dropdown */}
                <div className="sidebar-footer">
                    <div className="nav-item" style={{ cursor: 'default', opacity: 0.6 }}>
                        <span className="nav-icon">🔐</span>
                        <span className="nav-label" style={{ fontSize: '11px' }}>{user?.role}</span>
                    </div>
                </div>
            </aside>

            {/* Main area */}
            <div className="main-area">
                <header className="app-header">
                    <button
                        className="header-collapse-btn"
                        onClick={() => setMobileOpen(m => !m)}
                        aria-label="Toggle sidebar"
                    >
                        ☰
                    </button>

                    <div className="header-breadcrumb">
                        <strong>
                            Puresoft Dashboard
                            <span style={{ marginLeft: 'var(--space-2)', fontWeight: 'normal', fontSize: 'var(--text-sm)', color: 'var(--color-primary)', textTransform: 'capitalize' }}>
                                ({displayMode})
                            </span>
                        </strong>
                    </div>

                    <div className="header-actions">
                        {/* Account dropdown replaces the old static user block */}
                        <AccountMenu />
                    </div>
                </header>

                {isImpersonatingOther && (
                    <div style={{
                        background: 'var(--color-primary)',
                        color: 'white',
                        padding: 'var(--space-2) var(--space-4)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 500
                    }}>
                        <span>🎭 You are currently impersonating <strong>{user?.displayName}</strong>.</span>
                        <button
                            onClick={async () => {
                                stopImpersonation()
                                // Clear query cache to drop impersonated data, then hard reload
                                // Wait a tick for the store to persist the non-impersonated state
                                setTimeout(() => window.location.href = '/', 50)
                            }}
                            style={{
                                background: 'white',
                                color: 'var(--color-primary)',
                                border: 'none',
                                padding: 'var(--space-1) var(--space-3)',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: 'var(--text-xs)'
                            }}
                        >
                            Revert to Admin
                        </button>
                    </div>
                )}

                <main className="page-content">
                    <Outlet />
                </main>
            </div>
        </div>
    )
}
